// "Start with an agent" — the ZERO-ACCOUNT entry point on /signup.
//
// ConnectAgent (on /agent) hands an agent the code of an EXISTING account. This
// is the step before that: prompts you can paste into any coding agent when you
// have no account at all. The agent installs the CLI, creates the account, walks
// you through email verification (you relay the code — no browser), then builds
// and deploys.
//
// Verification is deliberately NOT automated away: only the person who owns the
// inbox can complete a signup. The agent does everything else.
//
// The prompts live in lib/agent-prompts.ts, shared with the landing page, so the
// two surfaces can't drift apart.

import { Alert, Card, Code, Group, Stack, Text, Title } from "@mantine/core";
import { IconRobot } from "@tabler/icons-react";
import { AGENT_PROMPTS, type AgentPrompt } from "../lib/agent-prompts.ts";
import { PromptPicker } from "./PromptPicker.tsx";

/** The "build a new app" prompt — the default hand-off. */
export function agentOnboardingPrompt(origin: string): string {
	const p = AGENT_PROMPTS.find((x) => x.id === "new") as AgentPrompt;
	return p.build(origin);
}

export function StartWithAgent() {
	return (
		<Card withBorder radius="md" padding="lg">
			<Group gap="xs" mb="xs">
				<IconRobot size={20} />
				<Title order={3}>Start with an agent</Title>
			</Group>
			<Text c="dimmed" size="sm" mb="md">
				No account needed. Paste one of these into Claude Code (or any coding agent
				with a shell) and it will create your account, help you verify your email,
				then build and deploy your app — end to end.
			</Text>

			<Stack gap="md">
				<PromptPicker prompts={AGENT_PROMPTS} maxHeight={200} />

				<Alert variant="light" color="blue" title="The one step your agent can't do">
					<Text size="sm">
						We email you a verification code. <b>Paste it back to your agent</b> and it
						runs <Code>s3p confirm --code …</Code>. That check is what stops bots from
						minting accounts — so it stays with you, the person who owns the inbox.
					</Text>
				</Alert>

				<Text size="xs" c="dimmed" ta="center">
					Your agent never sees an AWS key — only a revocable agent code.
				</Text>
			</Stack>
		</Card>
	);
}
